import React, { useState } from 'react'
import classnames from 'classnames'
import capitalize from 'lodash/capitalize'
import Return from '@Images/languages_view_return.svg'
import ArrowDown from '@Images/workspace_row_arrow_down.svg'
import ActionDropdown from '@SiteComponents/ActionDropdown'

import {
  IAccountUser
} from '@SiteModules/AccountUser/types'

import './AccountUserViewHeader.scss'

interface AccountUserViewHeaderProps {
  readonly fullAccountUserView: boolean
  readonly accountUser: IAccountUser | undefined
  readonly isOwnUser: boolean
  resetOpenedAccountUser(): void
  deleteAccountUser(): void
  changeAccountRole(newRole: string): void
}

const AccountUserViewHeader: React.FC<AccountUserViewHeaderProps> = props => {
  const {
    accountUser,
    fullAccountUserView,
    isOwnUser,
  } = props

  const [showDropdown, setShowDropdown] = useState(false)

  const roleElementId = 'account_user_view_header_role_button_wrapper'
  const classNameForClickOutsideListenerRoleDropdown = 'AccountUserViewHeader__role-button-wrapper-element'

  let accountUserNameOrEmail = ''
  let accountUserEmail = ''
  let accountUserRole = ''
  let accountUserCreatedAt = ''

  if (accountUser) {
    accountUserEmail = accountUser.attributes.email
    accountUserNameOrEmail = accountUser.attributes.name || accountUserEmail
    accountUserRole = accountUser.attributes.role
    accountUserCreatedAt = accountUser.attributes.createdAt
  }

  const isAdmin = accountUserRole === 'admin'
  const isMember = accountUserRole === 'member'

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown)
  }

  const changeRoleToAdmin = () => {
    if (isAdmin) { return }

    props.changeAccountRole('admin')
  }

  const changeRoleToMember = () => {
    if (isMember) { return }

    props.changeAccountRole('member')
  }

  const renderUserNameAndRole = () => {
    return (
      <div className="AccountUserViewHeader__user-name-role-wrapper-outer">
        <div className="AccountUserViewHeader__user-name-role-wrapper-inner">
          {fullAccountUserView && (
            <Return
              className="AccountUserViewHeader__return"
              onClick={props.resetOpenedAccountUser}
            />
          )}
          <div className="AccountUserViewHeader__user-name">
            {accountUserNameOrEmail}
          </div>
          <div
            className={classnames(
              'AccountUserViewHeader__role-badge',
              { 'AccountUserViewHeader__role-badge-admin': isAdmin },
              { 'AccountUserViewHeader__role-badge-member': isMember }
            )}
          >
            {capitalize(accountUserRole)}
          </div>
        </div>
      </div>
    )
  }

  const renderEmail = () => {
    return (
      <div className="AccountUserViewHeader__email">
        {accountUserEmail}
      </div>
    )
  }

  const renderInformation = () => {
    return (
      <div className="AccountUserViewHeader__information">
        <div className="AccountUserViewHeader__information-wrapper">
          <div className="AccountUserViewHeader__information-label">
            Account role
          </div>
          <div className="AccountUserViewHeader__information-content">
            {isOwnUser ? (
              <div>
                {capitalize(accountUserRole)} (you)
              </div>
            ) : (
              <div
                className={`AccountUserViewHeader__role-button-wrapper ${classNameForClickOutsideListenerRoleDropdown}`}
                id={roleElementId}
              >
                <div
                  className="AccountUserViewHeader__role-button"
                  onClick={toggleDropdown}
                >
                  <div>
                    {capitalize(accountUserRole)}
                  </div>
                  <ArrowDown />
                </div>
                {showDropdown && renderActionDropdown()}
              </div>
            )}
          </div>
        </div>
        <div className="AccountUserViewHeader__information-wrapper">
          <div className="AccountUserViewHeader__information-label">
            Joined on
          </div>
          <div className="AccountUserViewHeader__information-content">
            {accountUserCreatedAt}
          </div>
        </div>
      </div>
    )
  }

  const actionDropdownList = (
    <ul>
      <li
        className={classnames(
          'AccountUserViewHeader__role-action-dropdown-item',
          { 'AccountUserViewHeader__role-action-dropdown-item-current': isAdmin },
        )}
        onClick={changeRoleToAdmin}
      >
        <div className="AccountUserViewHeader__role-action-dropdown-item-text">
          Admin
        </div>
        <div className="AccountUserViewHeader__role-action-dropdown-item-explanation">
          Access and manage all account content
        </div>
      </li>
      <li
        className={classnames(
          'AccountUserViewHeader__role-action-dropdown-item',
          { 'AccountUserViewHeader__role-action-dropdown-item-current': isMember },
        )}
        onClick={changeRoleToMember}
      >
        <div className="AccountUserViewHeader__role-action-dropdown-item-text">
          Member
        </div>
        <div className="AccountUserViewHeader__role-action-dropdown-item-explanation">
          Access basic info and content they've been invited to
        </div>
      </li>
      <li
        className="AccountUserViewHeader__role-action-dropdown-item"
        onClick={props.deleteAccountUser}
      >
        <div className="AccountUserViewHeader__role-action-dropdown-item-text AccountUserViewHeader__role-action-dropdown-item-text-remove">
          Remove access
        </div>
      </li>
    </ul>
  )

  const renderActionDropdown = () => {
    return (
      <ActionDropdown
        showDropdown={showDropdown}
        dropdownList={actionDropdownList}
        classNamesForListContainer="AccountUserViewHeader__role-action-dropdown AccountUserView__element"
        classNameForClickOutsideListener={classNameForClickOutsideListenerRoleDropdown}
        classNameOfScrollContainer="AccountUserView"
        idOfDropdownElement={roleElementId}
        idOfPortalContainer="settings"
        dropdownOffsetX={274}
        dropdownOffsetY={5}
        dropdownWidth={410} // same width as AccountUserViewHeader__role-action-dropdown
        setShowDropdown={setShowDropdown}
      />
    )
  }

  return (
    <div
      className={classnames(
        'AccountUserViewHeader',
        { 'AccountUserViewHeader__full': fullAccountUserView}
      )}
    >
      {renderUserNameAndRole()}
      {renderEmail()}
      {renderInformation()}
    </div>
  )
}

export default AccountUserViewHeader
